<!-- Loading Spinner -->
<div class="flex items-center justify-center py-10" *ngIf="loading">
  <svg
    class="animate-spin h-8 w-8 text-indigo-600"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      class="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      stroke-width="4"
    ></circle>
    <path
      class="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4z"
    ></path>
  </svg>
</div>

<!-- Cards Section -->
<div
  class="@container p-4 sm:p-6 bg-white rounded-xl shadow-sm border border-gray-200"
  *ngIf="!loading"
>
  <div class="flex flex-wrap items-center justify-between gap-3 mb-6">
    <h2 class="text-xl font-semibold text-gray-900">User Cards</h2>
    <span
      [title]="migrateCardDisabledReason"
      [class.cursor-not-allowed]="!canMigrateCard || isMigratingCard"
      class="inline-block"
    >
      <button
        (click)="migrateCard()"
        [disabled]="!canMigrateCard || isMigratingCard"
        class="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:pointer-events-none shadow-sm transition"
      >
        {{ isMigratingCard ? 'Migrating...' : 'Migrate Card' }}
      </button>
    </span>
  </div>

  <!-- Cards Grid -->
  <div
    class="grid grid-cols-1 @xl:grid-cols-2 @4xl:grid-cols-3 gap-4 @xl:gap-6 sm:max-h-[75dvh] sm:overflow-y-auto sm:pr-2"
    *ngIf="cards?.length; else noCards"
  >
    <div
      class="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition"
      *ngFor="let card of cards; let i = index"
    >
      <!-- Card Header -->
      <div
        class="flex items-center bg-gray-50 px-4 py-3 border-b"
      >
        <div class="flex items-center space-x-3">
          <div class="bg-white rounded-lg p-2 border">
            <img
              src="https://cdn.visa.com/v2/assets/images/logos/visa/blue/logo.png"
              alt="Visa Logo"
              class="h-6 w-6 object-contain"
            />
          </div>
          <h3 class="text-sm font-semibold text-gray-900">Card {{ i + 1 }}</h3>
        </div>
      </div>

      <!-- Card Body -->
      <div class="px-4 py-4 space-y-2">
        <div class="flex justify-between gap-4 text-sm">
          <span class="text-gray-500">Status</span>
          <span
            class="px-2 py-0.5 rounded-full text-xs font-medium"
            [ngClass]="{
              'bg-green-100 text-green-700': card.status === 'active',
              'bg-yellow-100 text-yellow-700': card.status === 'locked',
              'bg-red-100 text-red-700': card.status === 'canceled',
            }"
          >
            {{ card.status | uppercase }}
          </span>
        </div>

        <div class="flex justify-between gap-4 text-sm">
          <span class="text-gray-500">ID</span>
          <span class="min-w-0 truncate font-mono text-gray-900" [title]="card.uuid">{{ card.uuid }}</span>
        </div>

        <div class="flex justify-between gap-4 text-sm">
          <span class="text-gray-500">Balance</span>
          <span class="font-semibold text-gray-900">${{ balance }}</span>
        </div>

        <div class="flex justify-between gap-4 text-sm text-gray-500">
          <span>Last 4 digits</span>
          <span>{{ card.last_four }}</span>
        </div>

        <div class="flex justify-between gap-4 text-sm text-gray-500">
          <span>Expiration</span>
          <span>{{ card.expiration_month }}/{{ card.expiration_year }}</span>
        </div>

        <div class="flex justify-between gap-4 text-sm text-gray-500">
          <span>Limit</span>
          <span class="text-right">{{ card.limit_amount }} {{ card.limit_frequency }}</span>
        </div>

        <!-- Action Buttons -->
        <div class="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap pt-3 mt-3 border-t border-gray-100">
          <button
            (click)="syncCard(card.uuid)"
            class="min-h-11 px-3 py-2 text-sm sm:min-h-0 sm:py-1 sm:text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
          >
            Sync Balance
          </button>
          <button
            (click)="lockCard(card.uuid)"
            class="min-h-11 px-3 py-2 text-sm sm:min-h-0 sm:py-1 sm:text-xs font-medium rounded-lg bg-yellow-50 text-yellow-700 hover:bg-yellow-100"
          >
            Lock
          </button>
          <button
            (click)="unlockCard(card.uuid)"
            class="min-h-11 px-3 py-2 text-sm sm:min-h-0 sm:py-1 sm:text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100"
          >
            Unlock
          </button>
          <button
            (click)="cancelCard(card.uuid)"
            class="min-h-11 px-3 py-2 text-sm sm:min-h-0 sm:py-1 sm:text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
          >
            Cancel
          </button>
          <button
            *ngIf="isComplianceAdmin"
            (click)="updateLimit(card.uuid)"
            class="min-h-11 px-3 py-2 text-sm sm:min-h-0 sm:py-1 sm:text-xs font-medium rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100"
          >
            Update Limit
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- No Cards State -->
  <ng-template #noCards>
    <div class="py-12 text-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="mx-auto h-12 w-12 text-gray-400"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"
        />
      </svg>

      <h3 class="mt-3 text-sm font-medium text-gray-900">
        This user doesn’t have any cards yet
      </h3>

      <p
        *ngIf="rainStatus === 'approved' && !rainTermsAccepted"
        class="mt-3 text-sm text-yellow-600"
      >
        Awaiting card terms acceptance to create card
      </p>

      <button
        *ngIf="canCreateCard"
        (click)="createCard()"
        class="mt-4 px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Create Card Account
      </button>
    </div>
  </ng-template>
</div>
